export const meta = {
  id: 3,
  title: 'Understanding React Rendering: When Does React Actually Update the DOM?',
  excerpt: 'Demystifying the Render vs. Commit phases. Why state changes don\'t immediately reflect in the DOM, and how batching optimizes performance.',
  date: 'December 18, 2023',
  category: 'Performance',
  tags: ['React', 'DOM', 'Rendering'],
  readingTime: '7 min read',
  difficulty: 'Beginner',
  isPublished: true
};

A common misconception is that calling a state setter immediately updates the DOM. In reality, React separates the calculation of updates from the actual DOM manipulation.

## Render Phase vs Commit Phase

The process of updating the UI in React is broken down into two distinct phases.

### 1. The Render Phase
During the **Render Phase**, React calls your components and compares the new output against the previous Virtual DOM (Reconciliation). This phase is pure and can be interrupted.

<Callout type="best-practice">
Because the render phase can be interrupted or restarted, you should never mutate external state or trigger side effects directly in the body of a functional component.
</Callout>

### 2. The Commit Phase
During the **Commit Phase**, React applies the calculated changes to the actual DOM. This phase is synchronous and cannot be interrupted.

Once the DOM is updated, React calls your `useLayoutEffect` and `useEffect` hooks.

## State Batching

To avoid unnecessary rendering, React batches multiple state updates into a single render pass.

```javascript
function handleClick() {
  setCount(c => c + 1);
  setFlag(f => !f);
  // React will only re-render once after this function completes!
}
```

In React 18, this batching was extended to asynchronous operations (like Promises or timeouts) via Automatic Batching.
